import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { debounce } from "lodash";
// @mui
import { Container, Typography, Stack, Chip, Switch, FormControlLabel, TextField, Button, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
// components
import { useSettingsContext } from '../../components/settings';
import CustomPagination from '../../components/custom-pagination';
import Iconify from '../../components/iconify';
import CustomModal from "../../components/custom-modal";
import CustomGrid from '../../components/custom-grid';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { searchLocation ,onPortalUpdateLocationStatus,onUpdateLocation } from '../../APIs/locations';
import {getLocationListColumns} from './locationsColumns'
import {LocationView} from './locationsView'

// ----------------------------------------------------------------------
import { useAuthContext } from '../../auth/useAuthContext';

export default function LocationsList() {
    const { themeStretch } = useSettingsContext();
    const axiosPrivate = useAxiosPrivate();    
    const [includeOnlyActive, setIncludeOnlyActive] = useState(false);
    const [totalLocation, setTotalLocation] = useState(0);
    const [locations, setLocations] = useState([]);    
    const [term, setTerm] = useState();
    const [fullWidth] = useState(true);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [openModal, setOpenModal] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState();
    const [readOnly, setReadOnly] = useState(true);
    
    const getLocations = (currentPage = 1) => {
        const request = { includeOnlyActive,term, size, page: currentPage ?? page }
        setPage(request.page);

        searchLocation(axiosPrivate, request, (response) => {
            const { data, total } = response;
            setLocations(data);
            setTotalLocation(total);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getLocations, [includeOnlyActive,term, size]);    

    const handleChange = (event) => {
        setIncludeOnlyActive(event.target.checked);
    };

    const handleSearch = (event) => {
        setTerm(event.target.value);
    }

    const handlePageChange = (pageIndex) => {
        setPage(pageIndex);
        getLocations(pageIndex);
    }

    const handlePageSizeChange = (pageSize) => {
        setSize(pageSize);
    }

    const onHandleClose = () => {
        setSelectedLocation();   
        setReadOnly(true);     
        setOpenModal(false);
    }

    const handleRowClick = ({ row }) => {
        setSelectedLocation(row);
        setOpenModal(true);
        setReadOnly(true);
    }

    const onEdit = (id) => {        
        const _locations = locations.find(c => c.id === id);
        setSelectedLocation(_locations);
        setReadOnly(false);
        setOpenModal(true);
    }

    const onAdd = () => {
        setReadOnly(false);
        setOpenModal(true);
    }

    const onDeactive = (id) => {
        onPortalUpdateLocationStatus(axiosPrivate, { id, isActive: false }, (response) => {
            if (response.status === 200) {
                getLocations(page);
            }
        });
    }

    const onActive = (id) => {
        onPortalUpdateLocationStatus(axiosPrivate, { id, isActive: true }, (response) => {
            if (response.status === 200) {
                getLocations(page);
            }
        });
    }

    const ChangeEvent = () => {
        getLocations(page);
        onHandleClose();
    }

    const debouncedChangeHandler = debounce(handleSearch, 500);

    const columns = getLocationListColumns({ onEdit, onActive, onDeactive  });

    return (
        <>
            <Helmet>
                <title>Locations | flok</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'xl'}>
                <Stack
                    flexGrow={1}
                    direction="column"
                    justifyContent="flex-start"
                    spacing={{ xs: 0.5, sm: 1.5 }}>
                    <Typography variant="h4" component="h4" paragraph>
                    Location <Chip label={totalLocation} />
                        <Button style={{ marginLeft: '2%', minWidth: 0 }} variant="outlined" onClick={onAdd}>+</Button>
                    </Typography>
                    <Stack
                        flexGrow={1}
                        direction="row"
                        justifyContent="flex-start"
                        spacing={{ xs: 0.5, sm: 1.5 }}>
                        <TextField
                            hiddenLabel
                            placeholder="Search..."
                            size="small"
                            onChange={debouncedChangeHandler}
                            fullWidth={fullWidth}
                            defaultValue={term}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeOnlyActive}
                                    onChange={handleChange}
                                    inputProps={{ 'aria-label': 'controlled' }} />
                            }
                            label="Hide Deleted Diagnosis" />
                    </Stack>
                    <CustomGrid>
                        <DataGrid
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'lightgray'
                                },
                                '.MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '&.MuiDataGrid-root': {
                                    border: 'none'
                                },
                                '.MuiDataGrid-row': {
                                    cursor: 'pointer'
                                },
                                '.MuiDataGrid-footerContainer': {
                                    display: 'none'
                                }
                            }}
                            rows={locations}
                            columns={columns}
                            disableColumnMenu
                            disableColumnFilter
                            disableRowSelectionOnClick
                            hideFooterPagination
                            onRowClick={handleRowClick}
                        />
                    </CustomGrid>
                    <CustomPagination pageSize={size} currentPage={page} total={totalLocation} handleChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} />
                    <CustomModal open={openModal} onClose={onHandleClose}>
                        <LocationView item={selectedLocation} onHandleClose={onHandleClose} readOnly={readOnly} onChange={ChangeEvent}/>
                    </CustomModal>
                </Stack>
            </Container>
        </>
    );
}

